import { ClipboardPaste, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { CalendarItem, Source, TaskDTO } from '@tempo/core';
import { api } from '../api';
import { combine, dateOf, dt, time, todayISO } from '../lib/format';
import { useAction, useFeatures, usePlaces, useSources } from '../lib/hooks';
import { Button, Field, Modal, NumberInput, Segmented, Switch } from './ui';

export type AddTab = 'event' | 'task' | 'shifts' | 'blackboard';

export function QuickAdd({ initial = 'event', date, onClose }: { initial?: AddTab; date?: string; onClose: () => void }) {
  const [tab, setTab] = useState<AddTab>(initial);
  if (tab === 'task') return <TaskForm onClose={onClose} header={<Tabs tab={tab} setTab={setTab} />} />;
  if (tab === 'shifts') return <ShiftsForm onClose={onClose} header={<Tabs tab={tab} setTab={setTab} />} />;
  if (tab === 'blackboard') return <BlackboardForm onClose={onClose} header={<Tabs tab={tab} setTab={setTab} />} />;
  return <EventForm date={date} onClose={onClose} header={<Tabs tab={tab} setTab={setTab} />} />;
}

function Tabs({ tab, setTab }: { tab: AddTab; setTab: (t: AddTab) => void }) {
  // Your Tempo gets deadlines from School Manager; a friend's pastes them from Blackboard.
  const { schoolManager } = useFeatures();
  return (
    <div style={{ marginBottom: 14 }}>
      <Segmented
        value={tab}
        onChange={setTab}
        options={[
          { value: 'event', label: 'Event' },
          { value: 'task', label: 'Task / study' },
          { value: 'shifts', label: 'Shifts' },
          ...(schoolManager ? [] : [{ value: 'blackboard' as const, label: 'Blackboard' }]),
        ]}
      />
    </div>
  );
}

// ───────────────────────────── event ─────────────────────────────

export function EventForm({ existing, date, onClose, header }: { existing?: CalendarItem; date?: string; onClose: () => void; header?: React.ReactNode }) {
  const sources = (useSources().data ?? []).filter((s) => s.kind === 'manual');
  const places = usePlaces().data ?? [];
  const roundedNow = Math.ceil(Date.now() / 1_800_000) * 1_800_000;
  const [title, setTitle] = useState(existing?.title ?? '');
  const [day, setDay] = useState(existing ? dateOf(existing.start) : (date ?? todayISO()));
  const [start, setStart] = useState(existing ? time(existing.start) : time(roundedNow));
  const [end, setEnd] = useState(existing ? time(existing.end) : time(roundedNow + 3_600_000));
  const [sourceId, setSourceId] = useState(existing?.sourceId ?? 'personal');
  const [placeId, setPlaceId] = useState(existing?.placeId ?? '');
  const [notes, setNotes] = useState(existing?.description ?? '');

  const save = useAction(
    () => {
      const s = combine(day, start);
      let e = combine(day, end);
      if (e <= s) e = dt(e).plus({ days: 1 }).toMillis(); // past midnight
      const body = { title: title.trim(), start: s, end: e, placeId: placeId || null, description: notes || null, sourceId };
      return existing ? api.patch(`/events/${existing.id}`, body) : api.post('/events', body);
    },
    existing ? 'Saved – plan updated' : 'Added – plan updated',
  );

  return (
    <Modal
      title={existing ? 'Edit event' : 'Add to calendar'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!title.trim()} loading={save.isPending} onClick={() => save.mutateAsync().then(onClose).catch(() => undefined)}>
            {existing ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      {header}
      <div className="stack">
        <Field label="What">
          <input className="input" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Dentist, meet Sam, football…" />
        </Field>
        <div className="grid-3">
          <Field label="Date">
            <input className="input" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </Field>
          <Field label="From">
            <input className="input" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label="To">
            <input className="input" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Where" hint="Pick a place to get travel time & a leave-by alert.">
            <select className="input" value={placeId} onChange={(e) => setPlaceId(e.target.value)}>
              <option value="">No travel needed</option>
              {places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Calendar">
            <select className="input" value={sourceId} onChange={(e) => setSourceId(e.target.value)} disabled={!!existing}>
              {sources.map((s: Source) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Notes">
          <textarea className="input" style={{ minHeight: 70 }} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ───────────────────────────── task ─────────────────────────────

export function TaskForm({ existing, onClose, header }: { existing?: TaskDTO; onClose: () => void; header?: React.ReactNode }) {
  const places = usePlaces().data ?? [];
  const external = existing?.source === 'school-manager';
  const [title, setTitle] = useState(existing?.title ?? '');
  const [module, setModule] = useState(existing?.module ?? '');
  const [dueDate, setDueDate] = useState(existing?.deadline ? dateOf(existing.deadline) : '');
  const [dueTime, setDueTime] = useState(existing?.deadline ? time(existing.deadline) : '16:00');
  const [hours, setHours] = useState<number | null>(existing ? Math.round((existing.remainingMin / 60) * 100) / 100 : 2);
  const [priority, setPriority] = useState(existing?.priority ?? 3);
  const [splittable, setSplittable] = useState(existing?.splittable ?? true);
  const [minChunk, setMinChunk] = useState<number | null>(existing?.minChunkMin ?? 30);
  const [maxChunk, setMaxChunk] = useState<number | null>(existing?.maxChunkMin ?? 120);
  const [maxPerDay, setMaxPerDay] = useState<number | null>(existing?.maxPerDayMin ?? null);
  const [startDate, setStartDate] = useState(existing?.earliestStart ? dateOf(existing.earliestStart) : '');
  const [placeId, setPlaceId] = useState(existing?.placeId ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [url, setUrl] = useState(existing?.url ?? '');
  const [advanced, setAdvanced] = useState(false);

  const save = useAction(
    () => {
      const body = {
        title: title.trim(),
        module: module.trim() || null,
        deadline: dueDate ? combine(dueDate, dueTime || '23:59') : null,
        remainingMin: Math.round((hours ?? 0) * 60),
        ...(existing ? {} : { estimatedMin: Math.round((hours ?? 0) * 60) }),
        priority,
        splittable,
        minChunkMin: minChunk ?? 30,
        maxChunkMin: Math.max(maxChunk ?? 120, minChunk ?? 30),
        maxPerDayMin: maxPerDay,
        earliestStart: startDate ? combine(startDate, '00:00') : null,
        placeId: placeId || null,
        notes: notes || null,
        url: url || null,
      };
      return existing ? api.patch(`/tasks/${existing.id}`, body) : api.post('/tasks', body);
    },
    existing ? 'Saved – sessions rescheduled' : 'Added – sessions scheduled',
  );

  return (
    <Modal
      title={existing ? 'Edit task' : 'Add a task'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!title.trim() || !hours} loading={save.isPending} onClick={() => save.mutateAsync().then(onClose).catch(() => undefined)}>
            {existing ? 'Save' : 'Add & schedule'}
          </Button>
        </>
      }
    >
      {header}
      <div className="stack">
        {external && (
          <div className="callout">
            School Manager owns this task's progress and deadline – changes here may be overwritten the next time it updates.
          </div>
        )}
        <Field label="What needs doing">
          <input className="input" autoFocus={!existing} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="FEEG1002 coursework, revise MATH1054…" />
        </Field>
        <div className="grid-3">
          <Field label="Due date" hint="Leave empty if no deadline">
            <input className="input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
          <Field label="Due time">
            <input className="input" type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} disabled={!dueDate} />
          </Field>
          <Field label={existing ? 'Hours left' : 'Hours needed'}>
            <NumberInput value={hours} onChange={setHours} min={0} step={0.25} />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Module">
            <input className="input" value={module} onChange={(e) => setModule(e.target.value.toUpperCase())} placeholder="FEEG1002" />
          </Field>
          <Field label="Where" hint="e.g. labs on campus">
            <select className="input" value={placeId} onChange={(e) => setPlaceId(e.target.value)}>
              <option value="">Anywhere</option>
              {places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Priority">
          <Segmented
            value={priority}
            onChange={setPriority}
            options={[
              { value: 1, label: 'Low' },
              { value: 2, label: '2' },
              { value: 3, label: 'Normal' },
              { value: 4, label: '4' },
              { value: 5, label: 'Urgent' },
            ]}
          />
        </Field>
        <Switch on={splittable} onChange={setSplittable} label="Split into several sessions" hint="Off = one sitting (e.g. a 45 min shop)" />
        <Button variant="ghost" size="sm" onClick={() => setAdvanced(!advanced)} style={{ alignSelf: 'flex-start' }}>
          {advanced ? 'Hide' : 'More'} options
        </Button>
        {advanced && (
          <div className="stack">
            {splittable && (
              <div className="grid-3">
                <Field label="Shortest session (min)">
                  <NumberInput value={minChunk} onChange={setMinChunk} min={5} step={5} />
                </Field>
                <Field label="Longest session (min)">
                  <NumberInput value={maxChunk} onChange={setMaxChunk} min={5} step={5} />
                </Field>
                <Field label="Max per day (min)">
                  <NumberInput value={maxPerDay} onChange={setMaxPerDay} min={5} step={15} placeholder="no limit" />
                </Field>
              </div>
            )}
            <Field label="Don't start before">
              <input className="input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field label="Link">
              <input className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Blackboard / brief URL" />
            </Field>
            <Field label="Notes">
              <textarea className="input" style={{ minHeight: 70 }} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ───────────────────────────── shifts ─────────────────────────────

interface ParsedShift {
  date: string;
  start: string;
  end: string;
  startMs: number;
  endMs: number;
  overnight: boolean;
  label: string | null;
  warning: string | null;
  line: string;
}

export function ShiftsForm({ onClose, header }: { onClose: () => void; header?: React.ReactNode }) {
  const sources = (useSources().data ?? []).filter((s) => s.kind === 'manual');
  const workSources = sources.filter((s) => s.category === 'work');
  const [sourceId, setSourceId] = useState(workSources[0]?.id ?? 'work');
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState<{ shifts: ParsedShift[]; skipped: { line: string; reason: string }[] } | null>(null);
  const [skip, setSkip] = useState<Set<number>>(new Set());

  const parse = useAction(async () => {
    const r = await api.post<{ shifts: ParsedShift[]; skipped: { line: string; reason: string }[] }>('/shifts/parse', { text });
    setParsed(r);
    setSkip(new Set());
    return r;
  });
  const chosen = useMemo(() => (parsed?.shifts ?? []).filter((_, i) => !skip.has(i)), [parsed, skip]);
  const save = useAction(
    () =>
      api.post<{ created: number }>('/shifts', {
        sourceId,
        shifts: chosen.map((s) => ({ start: s.startMs, end: s.endMs, title: s.label ? `Shift (${s.label})` : 'Shift' })),
      }),
    (r) => `Added ${r.created} shift${r.created === 1 ? '' : 's'} – plan updated`,
  );

  const pasteClipboard = async () => {
    try {
      setText(await navigator.clipboard.readText());
    } catch {
      /* clipboard blocked – user can paste manually */
    }
  };

  return (
    <Modal
      title="Add shifts"
      onClose={onClose}
      footer={
        parsed ? (
          <>
            <Button className="left" variant="ghost" onClick={() => setParsed(null)}>
              Back
            </Button>
            <Button variant="primary" disabled={!chosen.length} loading={save.isPending} onClick={() => save.mutateAsync().then(onClose).catch(() => undefined)}>
              Add {chosen.length} shift{chosen.length === 1 ? '' : 's'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={!text.trim()} loading={parse.isPending} onClick={() => parse.mutate()}>
              Read shifts
            </Button>
          </>
        )
      }
    >
      {header}
      {!parsed ? (
        <div className="stack">
          <p className="small subtle">
            Copy your rota from the staff app, a message or an email and paste it here. Most formats work: <code>Mon 26/10 17:00-23:00</code>,{' '}
            <code>Tue 27th Oct 5pm - close</code>, <code>Friday</code> then <code>6-11</code> on the next line…
          </p>
          <Field label="Rota">
            <textarea className="input mono" style={{ minHeight: 160 }} value={text} onChange={(e) => setText(e.target.value)} placeholder={'Mon 26/10 17:00-23:00\nWed 28/10 12:00-18:00\nSat 31/10 18:00-close'} />
          </Field>
          <div className="row wrap">
            <Button size="sm" onClick={pasteClipboard}>
              <ClipboardPaste size={15} /> Paste
            </Button>
            {workSources.length > 1 && (
              <select className="input sm" style={{ width: 'auto' }} value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
                {workSources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      ) : (
        <div className="stack tight">
          {parsed.shifts.length === 0 && <div className="callout warn">No shifts found. Each line needs a day/date and a time range like 17:00-23:00.</div>}
          {parsed.shifts.map((s, i) => (
            <label key={i} className="card flat row" style={{ padding: 10, cursor: 'pointer', marginTop: 0 }}>
              <input
                type="checkbox"
                checked={!skip.has(i)}
                onChange={() => {
                  const next = new Set(skip);
                  if (next.has(i)) next.delete(i);
                  else next.add(i);
                  setSkip(next);
                }}
              />
              <div className="grow">
                <div style={{ fontWeight: 650 }}>
                  {dt(s.startMs).toFormat('cccc d LLL')} · {s.start}–{s.end}
                  {s.overnight ? ' (+1)' : ''}
                </div>
                <div className="tiny faint truncate">
                  {s.label ? `${s.label} · ` : ''}
                  {s.line}
                </div>
                {s.warning && (
                  <div className="tiny" style={{ color: 'var(--warn)' }}>
                    <TriangleAlert size={12} /> {s.warning}
                  </div>
                )}
              </div>
            </label>
          ))}
          {parsed.skipped.length > 0 && (
            <details className="small faint">
              <summary>
                {parsed.skipped.length} line{parsed.skipped.length === 1 ? '' : 's'} skipped
              </summary>
              {parsed.skipped.map((s, i) => (
                <div key={i}>
                  {s.reason}: <code>{s.line}</code>
                </div>
              ))}
            </details>
          )}
        </div>
      )}
    </Modal>
  );
}

// ───────────────────────────── blackboard ─────────────────────────────

interface DueItem {
  externalId: string;
  title: string;
  module: string | null;
  deadline: number;
  kind: 'quiz' | 'assignment' | 'exam';
  estimatedMin: number;
  state: 'new' | 'changed' | 'same' | 'closed' | 'past';
}
const STATE_NOTE: Record<DueItem['state'], string> = {
  new: '',
  changed: 'already in Tasks – its title or deadline changed',
  same: 'already in Tasks',
  closed: "you've ticked this off",
  past: 'already past',
};

/** Blackboard → Calendar → Due Dates, copied and pasted: every quiz and assignment becomes a task with study time. */
export function BlackboardForm({ onClose, header }: { onClose: () => void; header?: React.ReactNode }) {
  const [text, setText] = useState('');
  const [items, setItems] = useState<DueItem[] | null>(null);
  const [skip, setSkip] = useState<Set<string>>(new Set());

  const read = useAction(async () => {
    const r = await api.post<{ items: DueItem[] }>('/import/blackboard', { text });
    setItems(r.items);
    setSkip(new Set());
    return r;
  });
  const actionable = (items ?? []).filter((i) => i.state === 'new' || i.state === 'changed');
  const chosen = actionable.filter((i) => !skip.has(i.externalId));
  const save = useAction(
    () => api.post<{ created: number; updated: number }>('/import/blackboard', { text, apply: true, skip: [...skip] }),
    (r) => [r.created && `Added ${r.created}`, r.updated && `updated ${r.updated}`].filter(Boolean).join(', ') + ' – study time planned',
  );

  const pasteClipboard = async () => {
    try {
      setText(await navigator.clipboard.readText());
    } catch {
      /* clipboard blocked – user can paste manually */
    }
  };

  return (
    <Modal
      title="Add from Blackboard"
      onClose={onClose}
      footer={
        items ? (
          <>
            <Button className="left" variant="ghost" onClick={() => setItems(null)}>
              Back
            </Button>
            <Button variant="primary" disabled={!chosen.length} loading={save.isPending} onClick={() => save.mutateAsync().then(onClose).catch(() => undefined)}>
              Add {chosen.length} to Tasks
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={!text.trim()} loading={read.isPending} onClick={() => read.mutate()}>
              Read due dates
            </Button>
          </>
        )
      }
    >
      {header}
      {!items ? (
        <div className="stack">
          <p className="small subtle">
            In Blackboard open <b>Calendar</b> → <b>Due Dates</b>, select the whole page (Ctrl+A, or long-press → Select all on a phone), copy it and paste it
            here. Each quiz and assignment becomes a task, and Tempo books study time before it's due. Paste it again whenever new work appears: what you
            already have is updated, never duplicated.
          </p>
          <Field label="Due Dates page">
            <textarea className="input mono" style={{ minHeight: 160 }} value={text} onChange={(e) => setText(e.target.value)} placeholder={'Quiz 3\nDue date: 06/11/2026, 23:59 (GMT) ∙ FEEG1002-…: FEEG1002 Mechanics…'} />
          </Field>
          <div className="row wrap">
            <Button size="sm" onClick={pasteClipboard}>
              <ClipboardPaste size={15} /> Paste
            </Button>
          </div>
        </div>
      ) : (
        <div className="stack tight">
          {items.length === 0 && (
            <div className="callout warn">Nothing due found. Copy the whole Due Dates page: each item needs its "Due date: DD/MM/YYYY, HH:MM" line.</div>
          )}
          {items.length > 0 && !actionable.length && <div className="callout">Everything here is already in Tasks.</div>}
          {items.map((it) => {
            const open = it.state === 'new' || it.state === 'changed';
            return (
              <label key={it.externalId} className="card flat row" style={{ padding: 10, cursor: open ? 'pointer' : 'default', marginTop: 0, opacity: open ? 1 : 0.6 }}>
                <input
                  type="checkbox"
                  disabled={!open}
                  checked={open && !skip.has(it.externalId)}
                  onChange={() => {
                    const next = new Set(skip);
                    if (next.has(it.externalId)) next.delete(it.externalId);
                    else next.add(it.externalId);
                    setSkip(next);
                  }}
                />
                <div className="grow">
                  <div style={{ fontWeight: 650 }}>{it.title}</div>
                  <div className="tiny faint">
                    {it.module ? `${it.module} · ` : ''}due {dt(it.deadline).toFormat('ccc d LLL, HH:mm')} · {it.kind}
                    {open ? ` · ${it.estimatedMin >= 60 ? `${Math.round(it.estimatedMin / 60)} h` : `${it.estimatedMin} min`} of study to start with` : ` · ${STATE_NOTE[it.state]}`}
                  </div>
                </div>
              </label>
            );
          })}
          {actionable.length > 0 && <p className="tiny faint">Change how long each one needs in Tasks once they're added.</p>}
        </div>
      )}
    </Modal>
  );
}
